import * as React from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Copy,
  DollarSign,
  FileText,
  HelpCircle,
  Loader2,
  Lock,
  MessageCircleReply,
  MessageSquare,
  RefreshCw,
  Send,
  Sparkles,
  Trash2,
  Undo2,
  UserRound,
  X,
  XCircle,
} from "lucide-react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ComboboxSelect,
  type ComboboxOption,
} from "@/components/ui/combobox-select";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { useUserProfile } from "@/hooks/useUserProfile";
import { api } from "@/lib/convexApi";
import {
  formatCurrency,
  formatRelativeTime,
  formatUtcDateOnly,
} from "@/lib/format";
import { toUserFacingErrorMessage } from "@/lib/errors";
import {
  canPermanentlyDeleteAccountingBills,
  hasPermission,
} from "@/lib/roles";
import { cn } from "@/lib/utils";
import {
  BillStatusBadge,
  dateInputToUtcMs,
  internalProfileLabel,
  isBillLockedStatus,
  isUsdCurrency,
  parseAmountInput,
  parseBillListFilters,
  utcMsToDateInput,
  type InternalProfileOption,
} from "../bill-shared";
import {
  QuickCreateVendorDialog,
  type QuickCreatedVendor,
} from "../quick-create-vendor-dialog";
import {
  AllocationsEditor,
  type AllocationRowData,
  type GlAccountOption,
} from "./allocations-editor";
import {
  AciAllocationsEditor,
  type AciAllocationRowData,
  type AciOption,
} from "./aci-allocations-editor";
import { BillAiPanel } from "./bill-ai-panel";
import { BillDetailSkeleton } from "./detail-skeleton";
import { DocumentPanel, type BillDocumentSummary } from "./document-panel";
import {
  OrderFreightComparison,
  type OrderFreight,
} from "./order-freight-comparison";

const NO_VENDOR = "__none__";
const UNASSIGNED = "__unassigned__";

interface BillEvent {
  _id: string;
  kind: string;
  actor: { kind: string; label?: string; userProfileId?: string };
  message?: string;
  data?: unknown;
  createdAt: number;
}

interface BillApproval {
  status: string;
  policyName: string;
  steps: { name?: string; approverProfileIds: string[] }[];
  currentStepIndex: number;
  revision: number;
  submittedBy: string;
  decisions: {
    stepIndex: number;
    decision: string;
    decidedBy: string;
    reason?: string;
    createdAt: number;
  }[];
}

export type SuggestionConfidence = "high" | "medium" | "low";

export interface SuggestedField<T> {
  value: T;
  confidence: SuggestionConfidence;
  evidence?: string;
}

export interface BillExtractionInfo {
  _id: string;
  status: "processing" | "succeeded" | "failed";
  attempt: number;
  model: string;
  autoAppliedFields?: string[];
  error?: string;
  createdAt: number;
  completedAt?: number;
  suggestions?: {
    vendorNameRaw?: SuggestedField<string>;
    vendorId?: string;
    invoiceNumber?: SuggestedField<string>;
    invoiceDate?: SuggestedField<number>;
    dueDate?: SuggestedField<number>;
    totalAmount?: SuggestedField<number>;
    currency?: SuggestedField<string>;
    allocations: {
      glAccountId?: string;
      glAccountName: string;
      memo: string;
      amount?: number;
      confidence: SuggestionConfidence;
    }[];
  };
}

interface BillDetail {
  bill: {
    _id: string;
    status: string;
    source: string;
    vendorId?: string;
    invoiceNumber?: string;
    invoiceDate?: number;
    dueDate?: number;
    currencyConfirmedUsd: boolean;
    totalAmount?: number;
    notes?: string;
    duplicateState?: string;
    assigneeProfileId?: string;
    vendorSnapshot?: { displayName: string; qboPayeeName: string };
    voidReason?: string;
    paidAt?: number;
    paymentReference?: string;
    openQuestionAt?: number;
    revision: number;
  };
  vendor: { _id: string; displayName: string; active: boolean } | null;
  relatedOrderFreight: OrderFreight | null;
  allocations: AllocationRowData[];
  aciAllocations: AciAllocationRowData[];
  documents: BillDocumentSummary[];
  events: BillEvent[];
  splitChildBillIds: string[];
  approval: BillApproval | null;
  duplicates: {
    _id: string;
    status: string;
    invoiceNumber?: string;
    totalAmount?: number;
    createdAt: number;
    match?: "invoice_number" | "amount";
  }[];
  submissionErrors: string[];
  overdue: boolean;
  viewerCanApprove: boolean;
  viewerCanReassign?: boolean;
  viewerCanComment?: boolean;
  extraction: BillExtractionInfo | null;
  intake: {
    fromAddress: string;
    subject?: string;
    receivedAt: number;
  } | null;
}

const EVENT_ICONS: Record<string, React.ComponentType<{ className?: string }>> =
  {
    created: FileText,
    comment: MessageSquare,
    question: HelpCircle,
    question_answered: MessageCircleReply,
    document_added: FileText,
    document_removed: X,
    fields_changed: FileText,
    allocations_changed: DollarSign,
    aci_allocations_changed: DollarSign,
    assignment_changed: UserRound,
    duplicate_resolved: Copy,
    extraction_attempt: FileText,
    submitted: Send,
    withdrawn: Undo2,
    reopened: Undo2,
    restored: Undo2,
    approved: CheckCircle2,
    rejected: XCircle,
    exported: Check,
    paid: DollarSign,
    voided: X,
  };

const EVENT_LABELS: Record<string, string> = {
  created: "created the bill",
  comment: "commented",
  question: "asked a question",
  question_answered: "answered a question",
  document_added: "added a document",
  document_removed: "removed a document",
  fields_changed: "edited bill fields",
  allocations_changed: "updated GL allocations",
  aci_allocations_changed: "updated the ACI cost coding",
  assignment_changed: "changed the assignee",
  duplicate_resolved: "resolved the duplicate flag",
  extraction_attempt: "ran document extraction",
  submitted: "submitted for approval",
  withdrawn: "withdrew the bill",
  reopened: "reopened the bill for edits",
  restored: "restored the bill to review",
  approved: "approved",
  rejected: "rejected",
  exported: "exported",
  paid: "marked the bill paid",
  voided: "voided the bill",
};

function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  requireReason,
  destructive,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  requireReason: boolean;
  destructive?: boolean;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const reasonFieldId = React.useId();

  React.useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  const handleConfirm = async () => {
    if (isSubmitting || (requireReason && !reason.trim())) return;
    setIsSubmitting(true);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } catch {
      // Error already surfaced via toast; keep the dialog open.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleConfirm();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor={reasonFieldId}>
              {requireReason ? "Reason (required)" : "Reason (optional)"}
            </Label>
            <Textarea
              id={reasonFieldId}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void handleConfirm();
                }
              }}
              rows={3}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant={destructive ? "destructive" : "default"}
              disabled={isSubmitting || (requireReason && !reason.trim())}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MarkPaidDialog({
  open,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (paidAt: number | undefined, reference: string) => Promise<void>;
}) {
  const [paidDate, setPaidDate] = React.useState("");
  const [reference, setReference] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setPaidDate("");
      setReference("");
    }
  }, [open]);

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onConfirm(
        paidDate ? dateInputToUtcMs(paidDate) : undefined,
        reference.trim(),
      );
      onOpenChange(false);
    } catch {
      // Error already surfaced via toast; keep the dialog open.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleConfirm();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>Mark bill paid</DialogTitle>
            <DialogDescription>
              Record when this exported bill was actually paid.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Paid date</Label>
              <DatePicker
                value={paidDate}
                onChange={setPaidDate}
                placeholder="Defaults to today"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="paid-reference">Payment reference</Label>
              <Input
                id="paid-reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Check #, ACH ID…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Mark paid
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface BillFieldsSnapshot {
  vendorId: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  totalAmount: string;
  currencyConfirmed: boolean;
  notes: string;
  relatedOrderNumber: string;
}

interface BillVendorOption {
  _id: string;
  displayName: string;
  qboPayeeName?: string;
  aliases?: string[];
  active: boolean;
}

function seedFromBill(
  bill: BillDetail["bill"],
  relatedOrderNumber: string,
): BillFieldsSnapshot {
  return {
    vendorId: bill.vendorId ?? NO_VENDOR,
    invoiceNumber: bill.invoiceNumber ?? "",
    invoiceDate: utcMsToDateInput(bill.invoiceDate),
    dueDate: utcMsToDateInput(bill.dueDate),
    totalAmount: bill.totalAmount !== undefined ? String(bill.totalAmount) : "",
    currencyConfirmed: bill.currencyConfirmedUsd,
    notes: bill.notes ?? "",
    relatedOrderNumber,
  };
}

function sameFieldsSnapshot(
  a: BillFieldsSnapshot,
  b: BillFieldsSnapshot,
): boolean {
  return (
    a.vendorId === b.vendorId &&
    a.invoiceNumber === b.invoiceNumber &&
    a.invoiceDate === b.invoiceDate &&
    a.dueDate === b.dueDate &&
    // Compare amounts numerically so "5" and "5.00" are the same edit state.
    (parseAmountInput(a.totalAmount) ?? a.totalAmount) ===
      (parseAmountInput(b.totalAmount) ?? b.totalAmount) &&
    a.currencyConfirmed === b.currencyConfirmed &&
    a.notes === b.notes &&
    a.relatedOrderNumber.trim().toUpperCase() ===
      b.relatedOrderNumber.trim().toUpperCase()
  );
}

function vendorNameMatchKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function billVendorMatchesName(
  vendor: Pick<BillVendorOption, "displayName" | "qboPayeeName" | "aliases">,
  candidateName: string,
): boolean {
  const candidateKey = vendorNameMatchKey(candidateName);
  return [
    vendor.displayName,
    vendor.qboPayeeName,
    ...(vendor.aliases ?? []),
  ].some(
    (knownName) =>
      !!knownName && vendorNameMatchKey(knownName) === candidateKey,
  );
}

export function BillFieldsForm({
  authUserId,
  detail,
  vendors,
  editable,
  canManageVendors,
  onRerunExtraction,
  onDirtyChange,
}: {
  authUserId: string | undefined;
  detail: BillDetail;
  vendors: BillVendorOption[] | undefined;
  editable: boolean;
  canManageVendors: boolean;
  onRerunExtraction?: () => void;
  /** Reports unsaved-edit state so the page can guard queue navigation. */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const updateBill = useMutation(api.accountingBills.updateBill);
  const addVendorAlias = useMutation(
    (api as any).accountingBillMasters.addVendorAlias,
  );
  const [aliasSaved, setAliasSaved] = React.useState(false);
  const [isSavingAlias, setIsSavingAlias] = React.useState(false);
  const { bill } = detail;
  const savedOrderNumber = detail.relatedOrderFreight?.orderNumber ?? "";
  const [relatedOrderNumber, setRelatedOrderNumber] = React.useState(savedOrderNumber);
  const latestSavedOrderNumberRef = React.useRef(savedOrderNumber);
  latestSavedOrderNumberRef.current = savedOrderNumber;
  const relatedOrderNumberRef = React.useRef(relatedOrderNumber);
  relatedOrderNumberRef.current = relatedOrderNumber;
  const normalizedOrderNumber = relatedOrderNumber.trim().toUpperCase();
  const orderNumberValid = /^W-\d+$/.test(normalizedOrderNumber);
  const needsOrderLookup =
    editable && normalizedOrderNumber !== savedOrderNumber && orderNumberValid;
  const orderLookup = useQuery(
    api.accountingBills.getOrderFreight,
    authUserId && needsOrderLookup
      ? { authUserId, orderNumber: normalizedOrderNumber }
      : "skip",
  ) as OrderFreight | null | undefined;
  const orderLookupPending = needsOrderLookup && orderLookup === undefined;
  const orderNumberError = !normalizedOrderNumber
    ? undefined
    : !orderNumberValid
      ? "Enter an order number in the format W-1234."
      : needsOrderLookup && orderLookup === null
        ? `No order found for ${normalizedOrderNumber}. Check the number and try again.`
        : undefined;
  const relatedOrderFreight =
    !editable || normalizedOrderNumber === savedOrderNumber
      ? detail.relatedOrderFreight
      : needsOrderLookup ? orderLookup : null;

  const [vendorId, setVendorId] = React.useState(bill.vendorId ?? NO_VENDOR);
  const [invoiceNumber, setInvoiceNumber] = React.useState(
    bill.invoiceNumber ?? "",
  );
  const [invoiceDate, setInvoiceDate] = React.useState(
    utcMsToDateInput(bill.invoiceDate),
  );
  const [dueDate, setDueDate] = React.useState(utcMsToDateInput(bill.dueDate));
  const [totalAmount, setTotalAmount] = React.useState(
    bill.totalAmount !== undefined ? String(bill.totalAmount) : "",
  );
  const [currencyConfirmed, setCurrencyConfirmed] = React.useState(
    bill.currencyConfirmedUsd,
  );

  const [notes, setNotes] = React.useState(bill.notes ?? "");
  const [isSaving, setIsSaving] = React.useState(false);
  const [conflict, setConflict] = React.useState(false);
  const [quickCreateName, setQuickCreateName] = React.useState<string | null>(
    null,
  );
  const [quickCreatedVendors, setQuickCreatedVendors] = React.useState<
    QuickCreatedVendor[]
  >([]);
  const quickCreateReturnFocusRef = React.useRef<HTMLElement | null>(null);
  const currentTotalAmount = parseAmountInput(totalAmount);
  const comparisonTotalAmount = editable
    ? currentTotalAmount
    : bill.totalAmount;

  React.useEffect(() => {
    setAliasSaved(false);
  }, [bill._id, detail.extraction?._id]);

  const current: BillFieldsSnapshot = {
    vendorId,
    invoiceNumber,
    invoiceDate,
    dueDate,
    totalAmount,
    currencyConfirmed,
    notes,
    relatedOrderNumber,
  };

  // Snapshot of the last server state we seeded from; differing local state
  // means unsaved edits that a revision-driven re-seed must NOT clobber.
  const seedRef = React.useRef<BillFieldsSnapshot>(
    seedFromBill(bill, savedOrderNumber),
  );
  const seededBillIdRef = React.useRef(bill._id);
  const dirty = !sameFieldsSnapshot(current, seedRef.current);
  const dirtyRef = React.useRef(dirty);
  dirtyRef.current = dirty;

  // A non-editable (locked) bill never reports dirty: its read-only view has
  // already discarded local edits, so they must not block queue navigation.
  React.useEffect(() => {
    onDirtyChange?.(editable && dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, editable, onDirtyChange]);

  const applySeed = React.useCallback((source: BillDetail["bill"], orderNumber: string) => {
    const seed = seedFromBill(source, orderNumber);
    setVendorId(seed.vendorId);
    setInvoiceNumber(seed.invoiceNumber);
    setInvoiceDate(seed.invoiceDate);
    setDueDate(seed.dueDate);
    setTotalAmount(seed.totalAmount);
    setCurrencyConfirmed(seed.currencyConfirmed);
    setNotes(seed.notes);
    setRelatedOrderNumber(seed.relatedOrderNumber);
    seedRef.current = seed;
    setConflict(false);
  }, []);

  // Re-seed when the server revision moves (another editor, an upload, a
  // workflow transition) — unless there are unsaved edits, in which case we
  // keep them and show a conflict notice instead of silently wiping the form.
  React.useEffect(() => {
    if (seededBillIdRef.current !== bill._id) {
      seededBillIdRef.current = bill._id;
      applySeed(bill, savedOrderNumber);
      return;
    }
    if (!dirtyRef.current) {
      applySeed(bill, savedOrderNumber);
    } else {
      setConflict(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bill._id, bill.revision, savedOrderNumber]);

  const handleSave = async () => {
    if (!authUserId) return;
    if (orderNumberError || orderLookupPending) {
      toast.error(orderNumberError ?? "Wait for the order lookup to finish");
      return;
    }
    const amount = parseAmountInput(totalAmount);
    if (totalAmount.trim() && amount === undefined) {
      toast.error("Total must be a number");
      return;
    }
    const orderNumberChanged =
      normalizedOrderNumber !== seedRef.current.relatedOrderNumber.trim().toUpperCase();
    setIsSaving(true);
    try {
      await updateBill({
        authUserId,
        billId: bill._id,
        vendorId: vendorId === NO_VENDOR ? null : vendorId,
        invoiceNumber,
        invoiceDate: invoiceDate ? (dateInputToUtcMs(invoiceDate) ?? null) : null,
        dueDate: dueDate ? (dateInputToUtcMs(dueDate) ?? null) : null,
        currencyConfirmedUsd: currencyConfirmed,
        totalAmount: amount ?? null,
        notes,
        ...(orderNumberChanged
          ? { relatedOrderNumber: normalizedOrderNumber || null }
          : {}),
      });
      // Our fields now ARE the server state; the revision bump this save causes
      // must not read as a conflict.
      seedRef.current = { ...current };
      // Convex can publish the updated notes/inferred order before the mutation
      // promise resolves. Adopt that match after save without overwriting an
      // order number the reviewer edited while the request was in flight.
      if (
        !orderNumberChanged &&
        relatedOrderNumberRef.current === current.relatedOrderNumber
      ) {
        const latestOrderNumber = latestSavedOrderNumberRef.current;
        setRelatedOrderNumber(latestOrderNumber);
        seedRef.current.relatedOrderNumber = latestOrderNumber;
      }
      setConflict(false);
      toast.success("Bill saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save bill");
    } finally {
      setIsSaving(false);
    }
  };

  const openQuickCreateVendor = React.useCallback((name: string) => {
    const normalizedName = name.trim().replace(/\s+/g, " ");
    if (!normalizedName) return;
    quickCreateReturnFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setQuickCreateName(normalizedName);
  }, []);

  const restoreQuickCreateFocus = React.useCallback(() => {
    const previousTarget = quickCreateReturnFocusRef.current;
    const fallbackTarget = document.getElementById("bill-vendor");
    const target =
      previousTarget?.isConnected && !previousTarget.hasAttribute("disabled")
        ? previousTarget
        : fallbackTarget;
    target?.focus();
    quickCreateReturnFocusRef.current = null;
  }, []);

  const handleVendorCreated = React.useCallback(
    (vendor: QuickCreatedVendor) => {
      setQuickCreatedVendors((currentVendors) =>
        currentVendors.some((current) => current._id === vendor._id)
          ? currentVendors
          : [...currentVendors, vendor],
      );
      setVendorId(vendor._id);
      setQuickCreateName(null);
    },
    [],
  );

  const extraction = detail.extraction;
  const suggestions =
    extraction?.status === "succeeded" ? extraction.suggestions : undefined;
  // Extraction saying the invoice is not in USD withdraws the default
  // confirmation once, so the reviewer has to tick it deliberately. Only on
  // arrival of a fresh non-USD reading — never fights a manual re-tick.
  const nonUsdSuggestion =
    extraction?.status === "succeeded" &&
    extraction.suggestions?.currency &&
    !isUsdCurrency(extraction.suggestions.currency.value)
      ? extraction._id
      : null;
  const correctedForExtractionRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!nonUsdSuggestion) return;
    if (correctedForExtractionRef.current === nonUsdSuggestion) return;
    correctedForExtractionRef.current = nonUsdSuggestion;
    setCurrencyConfirmed(false);
  }, [nonUsdSuggestion]);

  if (!editable) {
    return (
      <div className="space-y-2 text-sm">
        <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <Lock className="h-3 w-3" />
          Fields are locked while the bill is{" "}
          {bill.status.replace(/_/g, " ")}.
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          <dt className="text-muted-foreground">Vendor</dt>
          <dd>
            {detail.vendor?.displayName ??
              bill.vendorSnapshot?.displayName ??
              "—"}
          </dd>
          <dt className="text-muted-foreground">Invoice #</dt>
          <dd>{bill.invoiceNumber ?? "—"}</dd>
          <dt className="text-muted-foreground">Invoice date</dt>
          <dd>
            {bill.invoiceDate !== undefined
              ? formatUtcDateOnly(bill.invoiceDate, { month: "short" })
              : "—"}
          </dd>
          <dt className="text-muted-foreground">Due date</dt>
          <dd>
            {bill.dueDate !== undefined
              ? formatUtcDateOnly(bill.dueDate, { month: "short" })
              : "—"}
          </dd>
          <dt className="text-muted-foreground">Total</dt>
          <dd className="tabular-nums">
            {bill.totalAmount !== undefined
              ? formatCurrency(bill.totalAmount)
              : "—"}
          </dd>
          <dt className="text-muted-foreground">Currency</dt>
          <dd>{bill.currencyConfirmedUsd ? "USD (confirmed)" : "Unconfirmed"}</dd>
          {bill.notes && (
            <>
              <dt className="text-muted-foreground">Notes</dt>
              <dd className="whitespace-pre-wrap">{bill.notes}</dd>
            </>
          )}
          {bill.status === "paid" && (
            <>
              <dt className="text-muted-foreground">Paid</dt>
              <dd>
                {bill.paidAt !== undefined
                  ? formatUtcDateOnly(bill.paidAt, { month: "short" })
                  : "—"}
                {bill.paymentReference ? ` · ${bill.paymentReference}` : ""}
              </dd>
            </>
          )}
          {bill.status === "voided" && bill.voidReason && (
            <>
              <dt className="text-muted-foreground">Void reason</dt>
              <dd>{bill.voidReason}</dd>
            </>
          )}
        </dl>
        {relatedOrderFreight && (
          <OrderFreightComparison
            freight={relatedOrderFreight}
            invoiceAmount={bill.totalAmount}
            currencyConfirmed={bill.currencyConfirmedUsd}
          />
        )}
      </div>
    );
  }

  const availableVendors: BillVendorOption[] = [
    ...(vendors ?? []),
    ...quickCreatedVendors.filter(
      (createdVendor) =>
        !(vendors ?? []).some((vendor) => vendor._id === createdVendor._id),
    ),
  ];
  const activeVendors = availableVendors.filter(
    (vendor) => vendor.active || vendor._id === bill.vendorId,
  );
  const vendorOptions: ComboboxOption[] = [
    { value: NO_VENDOR, label: "No vendor" },
    ...activeVendors.map((vendor) => {
      const qboPayeeName = vendor.qboPayeeName?.trim();
      return {
        value: vendor._id,
        label: vendor.displayName,
        sublabel:
          qboPayeeName &&
          qboPayeeName.toLowerCase() !== vendor.displayName.trim().toLowerCase()
            ? `QBO: ${qboPayeeName}`
            : undefined,
        searchText: [qboPayeeName, ...(vendor.aliases ?? [])].join(" "),
      };
    }),
  ];
  const isVendorCreateMatch = (
    option: ComboboxOption,
    candidateName: string,
  ) => {
    const vendor = availableVendors.find(
      (availableVendor) => availableVendor._id === option.value,
    );
    return vendor
      ? billVendorMatchesName(vendor, candidateName)
      : vendorNameMatchKey(option.label) ===
          vendorNameMatchKey(candidateName);
  };
  const isVendorCreateAllowed = (candidateName: string) =>
    !availableVendors.some((vendor) =>
      billVendorMatchesName(vendor, candidateName),
    );

  const selectedVendorOption =
    vendorOptions.find((option) => option.value === vendorId) ?? null;
  const selectedVendor = availableVendors.find(
    (vendor) => vendor._id === vendorId,
  );
  const extractedVendorName = suggestions?.vendorNameRaw?.value;
  const extractedVendorAlreadyMatchesSelection =
    extractedVendorName &&
    selectedVendor &&
    billVendorMatchesName(selectedVendor, extractedVendorName);
  const suggestedVendor =
    suggestions?.vendorId &&
    availableVendors.find(
      (vendor) =>
        vendor._id === suggestions.vendorId &&
        (vendor.active || vendor._id === bill.vendorId),
    );
  const currentActiveVendorMatch =
    extractedVendorName &&
    availableVendors.find(
      (vendor) =>
        vendor.active && billVendorMatchesName(vendor, extractedVendorName),
    );
  const currentVendorCatalogHasMatch =
    !!extractedVendorName &&
    availableVendors.some((vendor) =>
      billVendorMatchesName(vendor, extractedVendorName),
    );
  const extractedVendorId =
    suggestedVendor
      ? suggestedVendor._id
      : extractedVendorAlreadyMatchesSelection && vendorId !== NO_VENDOR
        ? vendorId
        : currentActiveVendorMatch
          ? currentActiveVendorMatch._id
          : undefined;

  const applyAllSuggestions = () => {
    if (!suggestions) return;
    if (extractedVendorId) setVendorId(extractedVendorId);
    if (suggestions.invoiceNumber) setInvoiceNumber(suggestions.invoiceNumber.value);
    if (suggestions.invoiceDate) setInvoiceDate(utcMsToDateInput(suggestions.invoiceDate.value));
    if (suggestions.dueDate) setDueDate(utcMsToDateInput(suggestions.dueDate.value));
    if (suggestions.totalAmount) setTotalAmount(String(suggestions.totalAmount.value));
    // A currency the document disagrees with is the one case worth undoing:
    // USD is confirmed by default, so a foreign invoice would otherwise sail
    // through submission on a default nobody chose.
    if (suggestions.currency) {
      setCurrencyConfirmed(isUsdCurrency(suggestions.currency.value));
    }
  };

  // Alias capture: the AI read a vendor name that matched nothing, the
  // reviewer picked the right vendor — offer to remember the mapping so the
  // next invoice with this name resolves automatically.
  const aliasPromptVendor =
    editable &&
    !aliasSaved &&
    suggestions?.vendorNameRaw &&
    !suggestions.vendorId &&
    !currentVendorCatalogHasMatch &&
    vendorId !== NO_VENDOR &&
    !extractedVendorAlreadyMatchesSelection
      ? selectedVendorOption
      : null;

  const handleRememberAlias = async () => {
    if (!authUserId || !aliasPromptVendor || !suggestions?.vendorNameRaw) return;
    setIsSavingAlias(true);
    try {
      const result = await addVendorAlias({
        authUserId,
        vendorId,
        alias: suggestions.vendorNameRaw.value,
      });
      setAliasSaved(true);
      toast.success(
        result?.added
          ? `Future "${suggestions.vendorNameRaw.value}" invoices will match ${aliasPromptVendor.label} automatically`
          : "This name already matches that vendor",
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not save the alias",
      );
    } finally {
      setIsSavingAlias(false);
    }
  };

  // Undo of the server-side auto-apply: clears only the fields the
  // extraction filled AND whose value the reviewer has not changed since —
  // an edited field is the human's now and stays.
  const clearAutoApplied = async () => {
    if (!authUserId || !suggestions) return;
    const fields = extraction?.autoAppliedFields ?? [];
    if (fields.length === 0) return;
    const clearArgs: Record<string, unknown> = {
      authUserId,
      billId: bill._id,
    };
    if (
      fields.includes("vendorId") &&
      bill.vendorId &&
      bill.vendorId === suggestions.vendorId
    ) {
      clearArgs.vendorId = null;
    }
    if (
      fields.includes("invoiceNumber") &&
      bill.invoiceNumber === suggestions.invoiceNumber?.value.trim()
    ) {
      clearArgs.invoiceNumber = "";
    }
    if (
      fields.includes("invoiceDate") &&
      bill.invoiceDate === suggestions.invoiceDate?.value
    ) {
      clearArgs.invoiceDate = null;
    }
    if (
      fields.includes("dueDate") &&
      bill.dueDate === suggestions.dueDate?.value
    ) {
      clearArgs.dueDate = null;
    }
    if (
      fields.includes("totalAmount") &&
      bill.totalAmount === suggestions.totalAmount?.value
    ) {
      clearArgs.totalAmount = null;
    }
    if (Object.keys(clearArgs).length <= 2) {
      toast.info("AI-filled values were already edited — nothing to clear");
      return;
    }
    try {
      await updateBill(clearArgs);
      toast.success("Cleared AI-filled fields");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to clear fields",
      );
    }
  };

  return (
    <>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
      >
      {extraction && (
        <BillAiPanel
          extraction={extraction}
          suggestions={suggestions}
          vendorMatched={!!extractedVendorId}
          vendorLookupPending={vendors === undefined}
          onUseAll={applyAllSuggestions}
          onClearAutoApplied={
            (extraction.autoAppliedFields?.length ?? 0) > 0 && editable
              ? () => void clearAutoApplied()
              : undefined
          }
          onRerun={
            extraction.status !== "processing" ? onRerunExtraction : undefined
          }
          onCreateVendor={
            canManageVendors &&
            vendors !== undefined &&
            suggestions?.vendorNameRaw &&
            isVendorCreateAllowed(suggestions.vendorNameRaw.value)
              ? () =>
                  openQuickCreateVendor(suggestions.vendorNameRaw!.value)
              : undefined
          }
          onApplyVendor={() => {
            if (extractedVendorId) setVendorId(extractedVendorId);
          }}
          onApplyInvoiceNumber={() =>
            setInvoiceNumber(suggestions!.invoiceNumber!.value)
          }
          onApplyInvoiceDate={() =>
            setInvoiceDate(utcMsToDateInput(suggestions!.invoiceDate!.value))
          }
          onApplyDueDate={() =>
            setDueDate(utcMsToDateInput(suggestions!.dueDate!.value))
          }
          onApplyTotal={() =>
            setTotalAmount(String(suggestions!.totalAmount!.value))
          }
        />
      )}
      {conflict && dirty && (
        <div className="rounded-md border px-2.5 py-1.5 text-xs">
          <span className="font-medium">
            This bill was updated elsewhere while you were editing.
          </span>{" "}
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={() => applySeed(bill, savedOrderNumber)}
          >
            Load the latest (discards your edits)
          </button>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="bill-vendor">Vendor</Label>
          <ComboboxSelect
            id="bill-vendor"
            options={vendorOptions}
            value={vendorId}
            placeholder="Select vendor"
            searchPlaceholder="Type to filter vendors…"
            emptyText="No matching active vendor. Check Bills settings for inactive vendors."
            loading={vendors === undefined}
            className="font-normal"
            onCreateOption={
              canManageVendors ? openQuickCreateVendor : undefined
            }
            createOptionLabel="Create vendor"
            isCreateOptionAllowed={isVendorCreateAllowed}
            isCreateOptionMatch={isVendorCreateMatch}
            onValueChange={(value) => setVendorId(value || NO_VENDOR)}
          />
          {aliasPromptVendor && (
            <div className="flex items-start justify-between gap-2 rounded border px-2 py-1.5">
              <span className="text-muted-foreground text-xs">
                Invoice says “{suggestions!.vendorNameRaw!.value}”. Remember it
                as {aliasPromptVendor.label} for future invoices?
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 shrink-0 px-2 text-xs"
                disabled={isSavingAlias}
                onClick={() => void handleRememberAlias()}
              >
                {isSavingAlias ? "Saving…" : "Remember"}
              </Button>
            </div>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="bill-invoice-number">Invoice #</Label>
          <Input
            id="bill-invoice-number"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Invoice date</Label>
          <DatePicker value={invoiceDate} onChange={setInvoiceDate} />
        </div>
        <div className="space-y-1.5">
          <Label>Due date</Label>
          <DatePicker value={dueDate} onChange={setDueDate} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="bill-total">Total</Label>
          {/* Text + decimal keyboard, not type=number: money fields get no
              stepper arrows, and parseAmountInput accepts "$1,234.56". */}
          <div className="relative">
            <span className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm">
              $
            </span>
            <Input
              id="bill-total"
              type="text"
              inputMode="decimal"
              className="pl-7"
              placeholder="0.00"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Currency</Label>
          <div className="flex min-h-9 items-center gap-3">
            <label className="flex shrink-0 items-center gap-2 text-sm">
              <Checkbox
                checked={currencyConfirmed}
                onCheckedChange={(checked) =>
                  setCurrencyConfirmed(checked === true)
                }
              />
              Amounts are USD
            </label>
          </div>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="bill-order-number">Order number</Label>
        <Input
          id="bill-order-number"
          placeholder="W-1234"
          value={relatedOrderNumber}
          onChange={(event) => setRelatedOrderNumber(event.target.value)}
          aria-invalid={!!orderNumberError}
          aria-describedby="bill-order-help"
          autoComplete="off"
          spellCheck={false}
        />
        <p
          id="bill-order-help"
          className={cn(
            "text-xs",
            orderNumberError ? "text-destructive" : "text-muted-foreground",
          )}
          role="status"
        >
          {orderNumberError ?? (orderLookupPending
            ? "Looking up the order…"
            : "For carrier invoices, enter the W-number to compare freight amounts. Save fields to keep the link.")}
        </p>
        {relatedOrderFreight && (
          <OrderFreightComparison
            freight={relatedOrderFreight}
            invoiceAmount={comparisonTotalAmount}
            currencyConfirmed={currencyConfirmed}
          />
        )}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="bill-notes">Notes</Label>
        <Textarea
          id="bill-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        {dirty && !isSaving && (
          <span className="text-muted-foreground text-xs">Unsaved changes</span>
        )}
        {dirty && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isSaving}
            onClick={() => applySeed(bill, savedOrderNumber)}
          >
            Revert changes
          </Button>
        )}
        <Button type="submit" size="sm" disabled={isSaving || !dirty || orderLookupPending || !!orderNumberError}>
          {isSaving ? "Saving…" : "Save fields"}
        </Button>
      </div>
    </form>

      <QuickCreateVendorDialog
        open={quickCreateName !== null}
        initialName={quickCreateName ?? ""}
        authUserId={authUserId}
        onOpenChange={(open) => {
          if (!open) setQuickCreateName(null);
        }}
        onCreated={handleVendorCreated}
        onRestoreFocus={restoreQuickCreateFocus}
      />
    </>
  );
}

interface QueueRow {
  _id: string;
  dueDate?: number;
  createdAt: number;
}

export default function BillDetailPage() {
  const { billId } = useParams<{ billId: string }>();
  const { authUserId, profile, user } = useUserProfile();
  const { userRole } = useAuthIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  // The inbox carries its filter params into detail links; keeping them on
  // every navigation makes "back" return to the same view and lets this page
  // rebuild the exact working queue for prev/next.
  const listSearch = location.search;
  const myProfileId = (profile?._id as string | undefined) ?? undefined;

  const canEdit = hasPermission(userRole, "accounting:edit");
  // Master-data creation requires the stricter accounting:manage permission.
  const canManage = hasPermission(userRole, "accounting:manage");
  const canDeleteBill = canPermanentlyDeleteAccountingBills(
    userRole,
    user?.email as string | undefined,
  );

  const detail = useQuery(
    api.accountingBills.getBillDetail,
    authUserId && billId ? { authUserId, billId } : "skip",
  ) as BillDetail | null | undefined;

  const queueFilters = React.useMemo(
    () =>
      parseBillListFilters(new URLSearchParams(location.search), myProfileId),
    [location.search, myProfileId],
  );
  const queueRowsRaw = useQuery(
    api.accountingBills.listBills,
    authUserId ? { authUserId, ...queueFilters, limit: 500 } : "skip",
  ) as QueueRow[] | undefined;
  // Mirror the inbox's default sort (due date ascending, undated last) so
  // prev/next walks the queue in the order the user saw it.
  const queueRows = React.useMemo(() => {
    if (!queueRowsRaw) return undefined;
    return [...queueRowsRaw].sort((a, b) => {
      const dueA = a.dueDate ?? Number.MAX_SAFE_INTEGER;
      const dueB = b.dueDate ?? Number.MAX_SAFE_INTEGER;
      if (dueA !== dueB) return dueA - dueB;
      return b.createdAt - a.createdAt;
    });
  }, [queueRowsRaw]);
  const queueIndex = React.useMemo(
    () => (queueRows ?? []).findIndex((row) => row._id === billId),
    [queueRows, billId],
  );
  const prevBillId =
    queueRows && queueIndex > 0 ? queueRows[queueIndex - 1]?._id : undefined;
  const nextBillId =
    queueRows && queueIndex >= 0 && queueIndex < queueRows.length - 1
      ? queueRows[queueIndex + 1]?._id
      : undefined;

  const navigateToBill = React.useCallback(
    (id: string) => {
      navigate(`/admin/accounting/bills/${id}${listSearch}`);
    },
    [navigate, listSearch],
  );

  // Unsaved-edit state reported by the fields form and allocations editor.
  // Queue navigation (j/k, prev/next) must never silently discard edits;
  // action-driven auto-advance stays unguarded because the acted-on bill has
  // left the queue anyway.
  const fieldsDirtyRef = React.useRef(false);
  const allocationsDirtyRef = React.useRef(false);
  const aciDirtyRef = React.useRef(false);
  const handleFieldsDirtyChange = React.useCallback((dirty: boolean) => {
    fieldsDirtyRef.current = dirty;
  }, []);
  const handleAllocationsDirtyChange = React.useCallback((dirty: boolean) => {
    allocationsDirtyRef.current = dirty;
  }, []);
  const handleAciDirtyChange = React.useCallback((dirty: boolean) => {
    aciDirtyRef.current = dirty;
  }, []);
  const guardedNavigateToBill = React.useCallback(
    (id: string) => {
      if (
        fieldsDirtyRef.current ||
        allocationsDirtyRef.current ||
        aciDirtyRef.current
      ) {
        toast.warning("This bill has unsaved changes.", {
          description: "Save them, or discard and keep moving.",
          action: {
            label: "Discard & continue",
            onClick: () => navigateToBill(id),
          },
        });
        return;
      }
      navigateToBill(id);
    },
    [navigateToBill],
  );
  const canStartPdfSplit = React.useCallback(() => {
    if (
      fieldsDirtyRef.current ||
      allocationsDirtyRef.current ||
      aciDirtyRef.current
    ) {
      toast.warning("Save or discard this bill's changes before splitting its PDF.");
      return false;
    }
    return true;
  }, []);

  // j/k (or ]/[) walk the queue without touching the mouse. Skipped while the
  // focus is in a field or a dialog is open.
  const prevBillIdRef = React.useRef(prevBillId);
  const nextBillIdRef = React.useRef(nextBillId);
  prevBillIdRef.current = prevBillId;
  nextBillIdRef.current = nextBillId;
  React.useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (
        target?.closest(
          "input, textarea, select, [contenteditable=true], [role=dialog]",
        )
      ) {
        return;
      }
      if ((event.key === "j" || event.key === "]") && nextBillIdRef.current) {
        event.preventDefault();
        guardedNavigateToBill(nextBillIdRef.current);
      } else if (
        (event.key === "k" || event.key === "[") &&
        prevBillIdRef.current
      ) {
        event.preventDefault();
        guardedNavigateToBill(prevBillIdRef.current);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [guardedNavigateToBill]);

  const vendors = useQuery(
    api.accountingBillMasters.listVendors,
    authUserId ? { authUserId, includeInactive: true } : "skip",
  ) as (BillVendorOption & { defaultGlAccountId?: string })[] | undefined;

  const glAccounts = useQuery(
    api.accountingBillMasters.listGlAccounts,
    authUserId ? { authUserId, includeInactive: true } : "skip",
  ) as GlAccountOption[] | undefined;

  // Bills for a coffee often arrive after it sells out (storage, final
  // freight), so the picker always includes inactive cargos.
  const aciOptions = useQuery(
    api.accountingBillAci.listAciOptions,
    authUserId ? { authUserId, includeInactive: true } : "skip",
  ) as AciOption[] | undefined;

  const internalProfiles = useQuery(
    api.accountingBillMasters.listInternalProfiles,
    authUserId ? { authUserId } : "skip",
  ) as InternalProfileOption[] | undefined;

  const submitBill = useMutation(api.accountingBills.submitBill);
  const withdrawBill = useMutation(api.accountingBills.withdrawBill);
  const approveBill = useMutation(api.accountingBills.approveBill);
  const rejectBill = useMutation(api.accountingBills.rejectBill);
  const reopenBill = useMutation(api.accountingBills.reopenBill);
  const voidBill = useMutation(api.accountingBills.voidBill);
  const restoreVoidedBill = useMutation(api.accountingBills.restoreVoidedBill);
  const deleteBills = useAction(api.accountingBillDocuments.deleteBills);
  const markBillPaid = useMutation(api.accountingBills.markBillPaid);
  const assignBill = useMutation(api.accountingBills.assignBill);
  const addComment = useMutation(api.accountingBills.addComment);
  const askQuestion = useMutation(api.accountingBills.askQuestion);
  const answerQuestion = useMutation(api.accountingBills.answerQuestion);
  const resolveDuplicate = useMutation(api.accountingBills.resolveDuplicate);
  const requestExtraction = useMutation(
    api.accountingBillExtraction.requestBillExtraction,
  );

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [withdrawOpen, setWithdrawOpen] = React.useState(false);
  const [approveOpen, setApproveOpen] = React.useState(false);
  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [reopenOpen, setReopenOpen] = React.useState(false);
  const [voidOpen, setVoidOpen] = React.useState(false);
  const [restoreOpen, setRestoreOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [markPaidOpen, setMarkPaidOpen] = React.useState(false);
  const [duplicateOpen, setDuplicateOpen] = React.useState(false);
  const [comment, setComment] = React.useState("");
  const [isCommenting, setIsCommenting] = React.useState(false);
  const [isQuestioning, setIsQuestioning] = React.useState(false);
  const commentFieldRef = React.useRef<HTMLTextAreaElement>(null);

  const profileNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const profile of internalProfiles ?? []) {
      map.set(profile._id, internalProfileLabel(profile));
    }
    return map;
  }, [internalProfiles]);

  if (!billId) {
    return (
      <div className="text-muted-foreground p-6 text-sm">Bill not found.</div>
    );
  }

  if (detail === undefined) {
    return <BillDetailSkeleton />;
  }

  // The bill is gone — deleted here or in another tab. Not an error state.
  if (detail === null) {
    return (
      <div className="space-y-3 p-6">
        <p className="text-muted-foreground text-sm">
          This bill no longer exists.
        </p>
        <Button variant="outline" asChild>
          <Link to={{ pathname: "/admin/accounting/bills", search: listSearch }}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to bills
          </Link>
        </Button>
      </div>
    );
  }

  const { bill } = detail;
  const locked = isBillLockedStatus(bill.status);
  const editable = !locked && canEdit;
  const canReassign =
    (canEdit && ["draft", "needs_review"].includes(bill.status)) ||
    detail.viewerCanReassign;
  const canComment = canEdit || detail.viewerCanComment === true;
  const vendorName =
    detail.vendor?.displayName ?? bill.vendorSnapshot?.displayName;

  const run = async (fn: () => Promise<unknown>, successMessage: string) => {
    try {
      await fn();
      toast.success(successMessage);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Action failed");
      throw err;
    }
  };

  const handleDeleteBill = async () => {
    setIsDeleting(true);
    try {
      const result = await deleteBills({
        billIds: [billId],
        acknowledgement: "PERMANENTLY_DELETE_BILLS",
      });
      toast.success("Bill deleted");
      if (result.orphanedR2Objects > 0) {
        toast.error("The bill was deleted, but a stored file needs cleanup.");
      }
      navigate({ pathname: "/admin/accounting/bills", search: listSearch });
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(
          error,
          "Couldn't delete this bill. Refresh and try again.",
        ),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // After acting on a bill, move to the next one in the working queue so a
  // filtered view can be processed as one linear pass. Captured before the
  // mutation because the reactive queue drops the bill once its status leaves
  // the filter.
  const advanceAfterAction = (capturedNextId: string | undefined) => {
    if (capturedNextId) navigateToBill(capturedNextId);
  };

  const handleSubmit = async () => {
    if (!authUserId) return;
    // Submitting acts on the SAVED bill; unsaved local edits would be
    // silently ignored (and then wiped by the auto-advance). Force a save
    // or an explicit discard first.
    if (
      fieldsDirtyRef.current ||
      allocationsDirtyRef.current ||
      aciDirtyRef.current
    ) {
      toast.warning(
        "Save the unsaved edits on this bill before submitting — submit uses the last saved values.",
      );
      return;
    }
    const advanceTo = nextBillId;
    setIsSubmitting(true);
    try {
      await run(
        () => submitBill({ authUserId, billId }),
        "Submitted for approval",
      );
      advanceAfterAction(advanceTo);
    } catch {
      // toast already shown
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAnswer = async () => {
    if (!authUserId || !comment.trim()) return;
    setIsCommenting(true);
    try {
      await answerQuestion({ authUserId, billId, body: comment.trim() });
      setComment("");
      toast.success("Answer posted");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not answer");
    } finally {
      setIsCommenting(false);
    }
  };

  const handleComment = async () => {
    if (!authUserId || !comment.trim()) return;
    setIsCommenting(true);
    try {
      await addComment({ authUserId, billId, body: comment.trim() });
      setComment("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not comment");
    } finally {
      setIsCommenting(false);
    }
  };

  const handleAskQuestion = async () => {
    if (!authUserId || !comment.trim()) return;
    setIsQuestioning(true);
    try {
      await askQuestion({ authUserId, billId, body: comment.trim() });
      setComment("");
      toast.success("Question posted");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not post question",
      );
    } finally {
      setIsQuestioning(false);
    }
  };

  const awaitingResponse = bill.openQuestionAt !== undefined;
  const submitBlocked = detail.submissionErrors.length > 0;
  const sortedEvents = [...detail.events].sort(
    (a, b) => b.createdAt - a.createdAt,
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <Button variant="ghost" size="sm" className="-ml-2 px-2" asChild>
              <Link
                to={{ pathname: "/admin/accounting/bills", search: listSearch }}
              >
                <ArrowLeft className="mr-1 h-4 w-4" />
                Bills
              </Link>
            </Button>
            {queueRows && queueIndex >= 0 && queueRows.length > 1 && (
              <span className="text-muted-foreground flex items-center gap-0.5 text-xs">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  disabled={!prevBillId}
                  aria-label="Previous bill in queue (k)"
                  title="Previous bill (k)"
                  onClick={() => prevBillId && guardedNavigateToBill(prevBillId)}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="tabular-nums">
                  {queueIndex + 1} of {queueRows.length}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  disabled={!nextBillId}
                  aria-label="Next bill in queue (j)"
                  title="Next bill (j)"
                  onClick={() => nextBillId && guardedNavigateToBill(nextBillId)}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight">
              {bill.invoiceNumber ?? vendorName ?? "New bill"}
            </h1>
            <BillStatusBadge status={bill.status} />
            {detail.overdue && <Badge variant="destructive">Overdue</Badge>}
            {awaitingResponse && (
              <Badge className="border-blue-300 bg-blue-100 text-blue-900 dark:border-blue-800/60 dark:bg-blue-950/60 dark:text-blue-200">
                Awaiting response
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground text-sm">
            {vendorName ?? "No vendor"}
            {bill.totalAmount !== undefined
              ? ` · ${formatCurrency(bill.totalAmount)}`
              : ""}
            {bill.dueDate !== undefined
              ? ` · due ${formatUtcDateOnly(bill.dueDate, { month: "short" })}`
              : ""}
          </p>
          {detail.intake && (
            <p className="text-muted-foreground text-xs">
              Received by email from{" "}
              <span className="text-foreground font-medium">
                {detail.intake.fromAddress}
              </span>
              {" · "}
              {formatRelativeTime(detail.intake.receivedAt)}
              {detail.intake.subject ? ` · “${detail.intake.subject}”` : ""}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {bill.status === "needs_review" &&
            canEdit &&
            !detail.viewerCanApprove && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      onClick={handleSubmit}
                      disabled={isSubmitting || submitBlocked}
                    >
                      {isSubmitting ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="mr-2 h-4 w-4" />
                      )}
                      Submit for approval
                    </Button>
                  </span>
                </TooltipTrigger>
                {submitBlocked && (
                  <TooltipContent className="max-w-xs">
                    <ul className="list-disc pl-4">
                      {detail.submissionErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
          {bill.status === "pending_approval" && canEdit && (
            <Button variant="outline" onClick={() => setWithdrawOpen(true)}>
              <Undo2 className="mr-2 h-4 w-4" />
              Withdraw
            </Button>
          )}
          {["needs_review", "pending_approval"].includes(bill.status) &&
            detail.viewerCanApprove && (
            <>
              <Button
                onClick={() => setApproveOpen(true)}
                disabled={bill.status === "needs_review" && submitBlocked}
                title={
                  bill.status === "needs_review" && submitBlocked
                    ? detail.submissionErrors.join(" ")
                    : undefined
                }
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Approve
              </Button>
              {bill.status === "pending_approval" && (
                <Button
                  variant="destructive"
                  onClick={() => setRejectOpen(true)}
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Reject
                </Button>
              )}
            </>
          )}
          {bill.status === "approved" && canEdit && (
            <Button variant="outline" onClick={() => setReopenOpen(true)}>
              <Undo2 className="mr-2 h-4 w-4" />
              Reopen for edits
            </Button>
          )}
          {bill.status === "voided" &&
            detail.splitChildBillIds.length === 0 &&
            canEdit && (
              <Button variant="outline" onClick={() => setRestoreOpen(true)}>
                <Undo2 className="mr-2 h-4 w-4" />
                Restore bill
              </Button>
            )}
          {["draft", "needs_review"].includes(bill.status) && canEdit && (
            <Button variant="outline" onClick={() => setVoidOpen(true)}>
              <X className="mr-2 h-4 w-4" />
              Void
            </Button>
          )}
          {["draft", "needs_review", "voided"].includes(bill.status) &&
            canDeleteBill &&
            detail.splitChildBillIds.length === 0 && (
              <Button
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            )}
          {bill.status === "exported" && canEdit && (
            <Button onClick={() => setMarkPaidOpen(true)}>
              <DollarSign className="mr-2 h-4 w-4" />
              Mark paid
            </Button>
          )}
        </div>
      </div>

      {bill.status === "needs_review" && canEdit && submitBlocked && (
        <div className="rounded-md border p-3 text-sm">
          <p className="flex items-center gap-1.5 font-medium">
            <AlertTriangle className="text-destructive h-4 w-4" />
            Before this bill can be submitted
          </p>
          <ul className="text-muted-foreground mt-1.5 grid gap-1 pl-6 sm:grid-cols-2">
            {detail.submissionErrors.map((error, index) => (
              <li key={index} className="list-disc">
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {bill.status === "approved" && (
        <div className="flex items-center gap-1.5 rounded-md border p-3 text-sm">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <span>
            This bill is approved and ready to export from the inbox ("Export
            to QBO"). Reopen it if something still needs editing.
          </span>
        </div>
      )}

      {awaitingResponse && (
        <div className="rounded-md border p-3 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 font-medium">
              <HelpCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              Awaiting response — a question is open on this bill
              {bill.openQuestionAt !== undefined
                ? ` since ${formatRelativeTime(bill.openQuestionAt)}`
                : ""}
              .
            </p>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  commentFieldRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                  commentFieldRef.current?.focus();
                }}
              >
                <MessageCircleReply className="mr-2 h-4 w-4" />
                Answer
              </Button>
            )}
          </div>
        </div>
      )}

      {bill.duplicateState === "suspected" && (
        <div className="rounded-md border p-3 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 font-medium">
              <Copy className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              Possible duplicate — another bill has the same invoice number,
              the same vendor and amount, or an identical attachment.
            </p>
            {editable && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDuplicateOpen(true)}
              >
                Not a duplicate
              </Button>
            )}
          </div>
          {detail.duplicates.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 pl-6">
              {detail.duplicates.map((duplicate) => (
                <li key={duplicate._id}>
                  {/* New tab on purpose: comparing duplicates side by side
                      should not lose the bill being reviewed. */}
                  <Link
                    className="underline underline-offset-2"
                    to={`/admin/accounting/bills/${duplicate._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {duplicate.invoiceNumber ?? "(no invoice #)"}
                  </Link>{" "}
                  — {duplicate.status.replace(/_/g, " ")}
                  {duplicate.totalAmount !== undefined
                    ? ` · ${formatCurrency(duplicate.totalAmount)}`
                    : ""}
                  {duplicate.match === "amount"
                    ? " · same amount"
                    : duplicate.match === "invoice_number"
                      ? " · same invoice #"
                      : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="grid items-start gap-4 lg:grid-cols-[55%_1fr]">
        {/* Sticky, viewport-height document: the thing being verified stays
            on screen while the form column scrolls. */}
        <div className="lg:sticky lg:top-4 lg:h-[calc(100vh-7rem)]">
          <DocumentPanel
            billId={billId}
            documents={detail.documents}
            canEdit={editable}
            beforeSplit={canStartPdfSplit}
            onSplitComplete={navigateToBill}
          />
        </div>

        <Card className="block space-y-4 rounded-xl p-4">
          <BillFieldsForm
            authUserId={authUserId ?? undefined}
            detail={detail}
            vendors={vendors}
            editable={editable}
            canManageVendors={canManage}
            onDirtyChange={handleFieldsDirtyChange}
            onRerunExtraction={
              editable && authUserId
                ? () => {
                    void requestExtraction({ authUserId, billId }).catch(
                      (err) =>
                        toast.error(
                          err instanceof Error
                            ? err.message
                            : "Could not start suggestions",
                        ),
                    );
                  }
                : undefined
            }
          />

          <Separator />

          <AllocationsEditor
            authUserId={authUserId ?? undefined}
            billId={billId}
            billRevision={bill.revision}
            allocations={detail.allocations}
            glAccounts={glAccounts}
            vendorDefaultGlAccountId={
              vendors?.find((vendor) => vendor._id === bill.vendorId)
                ?.defaultGlAccountId
            }
            totalAmount={bill.totalAmount}
            canEdit={editable}
            onDirtyChange={handleAllocationsDirtyChange}
            suggestedAllocations={
              detail.extraction?.status === "succeeded"
                ? (detail.extraction.suggestions?.allocations ?? [])
                : []
            }
          />

          <Separator />

          <AciAllocationsEditor
            authUserId={authUserId ?? undefined}
            billId={billId}
            billRevision={bill.revision}
            // `?? []` guards the deploy window where a not-yet-updated backend
            // response has no aciAllocations field.
            allocations={detail.aciAllocations ?? []}
            aciOptions={aciOptions}
            totalAmount={bill.totalAmount}
            canEdit={editable}
            onDirtyChange={handleAciDirtyChange}
          />

          <Separator />

          <div className="space-y-1.5">
            <Label htmlFor="bill-assignee">Assignee</Label>
            <Select
              value={bill.assigneeProfileId ?? UNASSIGNED}
              disabled={!canReassign}
              onValueChange={async (value) => {
                if (!authUserId) return;
                try {
                  await assignBill({
                    authUserId,
                    billId,
                    assigneeProfileId: value === UNASSIGNED ? null : value,
                  });
                  toast.success("Assignee updated");
                } catch (err) {
                  toast.error(
                    err instanceof Error ? err.message : "Could not assign",
                  );
                }
              }}
            >
              <SelectTrigger id="bill-assignee" className="w-64">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                {(internalProfiles ?? []).map((profile) => (
                  <SelectItem key={profile._id} value={profile._id}>
                    {internalProfileLabel(profile)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {detail.approval && (
            <>
              <Separator />
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">
                  Approval — {detail.approval.policyName}
                  <span className="text-muted-foreground ml-2 text-xs font-normal">
                    {detail.approval.status}
                  </span>
                </h3>
                <ol className="space-y-1 text-sm">
                  {detail.approval.steps.map((step, index) => {
                    const isCurrent =
                      detail.approval!.status === "pending" &&
                      index === detail.approval!.currentStepIndex;
                    const isDone =
                      detail.approval!.status === "approved" ||
                      index < detail.approval!.currentStepIndex;
                    return (
                      <li
                        key={index}
                        className={cn(
                          "flex items-center gap-2 rounded border px-2 py-1",
                          isCurrent &&
                            "border-primary bg-primary/5 text-foreground",
                          isDone && "text-muted-foreground",
                        )}
                      >
                        {isDone ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                        ) : (
                          <span className="text-xs tabular-nums">
                            {index + 1}.
                          </span>
                        )}
                        <span className="min-w-0 flex-1 truncate">
                          {step.name ?? `Step ${index + 1}`}
                          <span className="text-muted-foreground">
                            {" "}
                            —{" "}
                            {step.approverProfileIds
                              .map(
                                (id) => profileNameById.get(id) ?? "Unknown",
                              )
                              .join(", ")}
                          </span>
                        </span>
                        {isCurrent && (
                          <Badge variant="secondary">Current</Badge>
                        )}
                      </li>
                    );
                  })}
                </ol>
                {detail.approval.decisions.length > 0 && (
                  <div className="space-y-1 text-xs">
                    {detail.approval.decisions.map((decision, index) => (
                      <p key={index} className="text-muted-foreground">
                        <span
                          className={cn(
                            "font-medium",
                            decision.decision === "approved"
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-destructive",
                          )}
                        >
                          {decision.decision}
                        </span>{" "}
                        by {profileNameById.get(decision.decidedBy) ?? "Unknown"}{" "}
                        on step {decision.stepIndex + 1} ·{" "}
                        {formatRelativeTime(decision.createdAt)}
                        {decision.reason ? ` — ${decision.reason}` : ""}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <Separator />

          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Activity</h3>
            {canComment && (
              <div className="space-y-2">
                <Textarea
                  ref={commentFieldRef}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  aria-label="Comment"
                  placeholder={
                    canEdit && awaitingResponse
                      ? "Type your answer to the open question…"
                      : canEdit
                        ? "Add a comment or ask a question…"
                        : "Add a comment…"
                  }
                  rows={2}
                />
                <div className="flex flex-wrap justify-end gap-2">
                  {canEdit && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={
                        isQuestioning ||
                        isCommenting ||
                        awaitingResponse ||
                        !comment.trim()
                      }
                      onClick={handleAskQuestion}
                      title={
                        awaitingResponse
                          ? "A question is already open — answer it first"
                          : undefined
                      }
                    >
                      <HelpCircle className="mr-2 h-4 w-4" />
                      {isQuestioning ? "Posting…" : "Ask question"}
                    </Button>
                  )}
                  <Button
                    variant={awaitingResponse ? "outline" : "default"}
                    size="sm"
                    disabled={isCommenting || isQuestioning || !comment.trim()}
                    onClick={handleComment}
                  >
                    Comment
                  </Button>
                  {canEdit && awaitingResponse && (
                    <Button
                      size="sm"
                      disabled={
                        isCommenting || isQuestioning || !comment.trim()
                      }
                      onClick={handleAnswer}
                    >
                      <MessageCircleReply className="mr-2 h-4 w-4" />
                      {isCommenting ? "Posting…" : "Answer question"}
                    </Button>
                  )}
                </div>
              </div>
            )}
            <div className="max-h-96 space-y-2 overflow-y-auto pr-1">
              {sortedEvents.length === 0 ? (
                <p className="text-muted-foreground text-sm">No activity yet.</p>
              ) : (
                sortedEvents.map((event) => {
                  const Icon = EVENT_ICONS[event.kind] ?? FileText;
                  return (
                    <div key={event._id} className="flex gap-2 text-sm">
                      <Icon className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                      <div className="min-w-0">
                        <p>
                          <span className="font-medium">
                            {event.actor.label ?? event.actor.kind}
                          </span>{" "}
                          <span className="text-muted-foreground">
                            {EVENT_LABELS[event.kind] ?? event.kind}
                          </span>
                        </p>
                        {event.message && (
                          <p className="text-muted-foreground whitespace-pre-wrap break-words">
                            {event.message}
                          </p>
                        )}
                        <p className="text-muted-foreground/70 text-xs">
                          {formatRelativeTime(event.createdAt)}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Card>
      </div>

      <ReasonDialog
        open={withdrawOpen}
        onOpenChange={setWithdrawOpen}
        title="Withdraw from approval"
        description="The bill returns to review and can be edited again."
        confirmLabel="Withdraw"
        requireReason={false}
        onConfirm={async (reason) => {
          if (!authUserId) return;
          await run(
            () =>
              withdrawBill({
                authUserId,
                billId,
                reason: reason || undefined,
              }),
            "Bill withdrawn",
          );
        }}
      />

      <ReasonDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        title="Approve bill"
        description="Approve this assigned bill or the current step of its approval route."
        confirmLabel="Approve"
        requireReason={false}
        onConfirm={async (reason) => {
          if (!authUserId) return;
          const advanceTo = nextBillId;
          await run(
            () =>
              approveBill({
                authUserId,
                billId,
                comment: reason || undefined,
              }),
            "Bill approved",
          );
          advanceAfterAction(advanceTo);
        }}
      />

      <ReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Reject bill"
        description="The bill returns to review. A reason is required."
        confirmLabel="Reject"
        requireReason
        destructive
        onConfirm={async (reason) => {
          if (!authUserId) return;
          const advanceTo = nextBillId;
          await run(
            () => rejectBill({ authUserId, billId, reason }),
            "Bill rejected",
          );
          advanceAfterAction(advanceTo);
        }}
      />

      <ReasonDialog
        open={reopenOpen}
        onOpenChange={setReopenOpen}
        title="Reopen for edits"
        description="The approved bill returns to review so it can be edited and resubmitted. A reason is required."
        confirmLabel="Reopen"
        requireReason
        onConfirm={async (reason) => {
          if (!authUserId) return;
          await run(
            () => reopenBill({ authUserId, billId, reason }),
            "Bill reopened",
          );
        }}
      />

      <ReasonDialog
        open={voidOpen}
        onOpenChange={setVoidOpen}
        title="Void bill"
        description="Only this bill is voided; other bills created from the same PDF keep their current status. The voided bill is kept for the record but leaves every queue. A reason is required."
        confirmLabel="Void bill"
        requireReason
        destructive
        onConfirm={async (reason) => {
          if (!authUserId) return;
          const advanceTo = nextBillId;
          await run(() => voidBill({ authUserId, billId, reason }), "Bill voided");
          if (advanceTo) {
            navigateToBill(advanceTo);
          } else {
            navigate({
              pathname: "/admin/accounting/bills",
              search: listSearch,
            });
          }
        }}
      />

      <ReasonDialog
        open={restoreOpen}
        onOpenChange={setRestoreOpen}
        title="Restore voided bill"
        description="Only this bill returns to Needs review so it can be edited and resubmitted. Other bills created from the same PDF are not changed. A reason is required."
        confirmLabel="Restore bill"
        requireReason
        onConfirm={async (reason) => {
          if (!authUserId) return;
          await run(
            () => restoreVoidedBill({ authUserId, billId, reason }),
            "Bill restored to review",
          );
        }}
      />

      <AlertDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (!isDeleting) setDeleteOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this bill permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes bill {bill.invoiceNumber ?? "without an invoice number"},
              its documents, allocations, and AI suggestions. It cannot be
              undone. Email intake and audit evidence remain protected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={isDeleting}
              onClick={(event) => {
                event.preventDefault();
                void handleDeleteBill();
              }}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                "Delete permanently"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ReasonDialog
        open={duplicateOpen}
        onOpenChange={setDuplicateOpen}
        title="Confirm this is not a duplicate"
        description="Explain why the matching bill is different (e.g. a distinct delivery under the same invoice number)."
        confirmLabel="Not a duplicate"
        requireReason
        onConfirm={async (reason) => {
          if (!authUserId) return;
          await run(
            () => resolveDuplicate({ authUserId, billId, reason }),
            "Duplicate flag cleared",
          );
        }}
      />

      <MarkPaidDialog
        open={markPaidOpen}
        onOpenChange={setMarkPaidOpen}
        onConfirm={async (paidAt, reference) => {
          if (!authUserId) return;
          await run(
            () =>
              markBillPaid({
                authUserId,
                billId,
                paidAt,
                paymentReference: reference || undefined,
              }),
            "Bill marked paid",
          );
        }}
      />
    </div>
  );
}
